import { ClippingStudioEditorMode } from "./clipping-studio-editor-mode";
import { ClippingStudioMedia } from "./clipping-studio-media";
import { ClippingStudioClip } from "./clipping-studio-clip";
import { ClippingStudioMarker } from "./clipping-studio-marker";
import {
  ClippingStudioSubtitleEvent,
  ClippingStudioTextEvent,
  ClippingStudioGraphicEvent,
  ClippingStudioAudioEvent,
  DestinationVariant,
  ReframeConfig,
  ReframeKeyframe,
  normalizeSegmentReframes,
  AppendedMamSegment,
} from "./clipping-studio-project";
import { ClippingStudioTranscriptTrack, ClippingStudioSubtitleStyle } from "./clipping-studio-transcript";
import { ClippingStudioMainAudioTrack } from "./clipping-studio-main-audio-track";

/** Snapshot of state fields that are persisted and undoable. */
export interface ClippingStudioUndoableSnapshot {
  projectTitle: string;
  clips: ClippingStudioClip[];
  markers: ClippingStudioMarker[];
  subtitles: ClippingStudioSubtitleEvent[];
  subtitleEdits: Record<string, ClippingStudioSubtitleEvent[]> | undefined;
  subtitleStyle: ClippingStudioSubtitleStyle | null;
  text: ClippingStudioTextEvent[];
  graphics: ClippingStudioGraphicEvent[];
  audio: ClippingStudioAudioEvent[];
  destinationVariants: Record<string, DestinationVariant>;
  appendedMamSegments: AppendedMamSegment[];
}

export interface ClippingStudioState {
  mode: ClippingStudioEditorMode;
  media: ClippingStudioMedia | null;
  /** Backend project id when loaded from API; null when using mock or not yet loaded. Used for find-clip recent prompts API. */
  projectId: number | null;
  /** Main source media JSON (round-tripped from API; required on save). */
  mainSource: string | null;
  /** Find-clip recent prompts JSON array (round-tripped from API; required on save). */
  findClipRecentPrompts: string | null;
  /** Project fps (from API or media). Used on save; fallback to media.fps or 30. */
  fps: number | null;
  /** Project display title (persisted as project.title). */
  projectTitle: string;
  clips: ClippingStudioClip[];
  markers: ClippingStudioMarker[];
  subtitles: ClippingStudioSubtitleEvent[];
  text: ClippingStudioTextEvent[];
  graphics: ClippingStudioGraphicEvent[];
  audio: ClippingStudioAudioEvent[];
  /**
   * Linked audio tracks for the main source (from getMediaInfo).
   * Runtime-only; used by the main audio track picker.
   */
  mainAudioTracks: ClippingStudioMainAudioTrack[];
  /** Per-destination variant data (overrides + destination-only elements). */
  destinationVariants: Record<string, DestinationVariant>;
  /** MAM video segments appended to the source timeline (Source mode). */
  appendedMamSegments: AppendedMamSegment[];
  pendingInMs: number | null;
  pendingOutMs: number | null;
  selectedClipId: string | null;
  selectedMarkerId: string | null;
  isPlaying: boolean;
  currentTimeMs: number;
  transcriptTracks: ClippingStudioTranscriptTrack[];
  sourceAudioLanguage: string;
  activeTranscriptLanguage: string;
  isRegeneratingTranscript: boolean;
  /** True while transcript tracks are being loaded from API. */
  loadingTranscriptTracks: boolean;
  /** Error message when transcript tracks load failed. */
  transcriptLoadError: string | null;
  /** True while subtitle tracks are being loaded from API. */
  loadingSubtitleTracks: boolean;
  /** Error message when subtitle tracks load failed. */
  subtitleLoadError: string | null;
  /** Subtitle style applied to all subtitle clips (from style panel). */
  subtitleStyle: ClippingStudioSubtitleStyle | null;
  /** Currently selected graphic for the inline editor (null = catalogue view). */
  selectedGraphicId: string | null;
  /** Raw persisted clip preview payload: legacy data URL or v1 JSON model. */
  clipPreview: string | null;
  /** Currently selected text for the styling panel (null = catalogue view). */
  selectedTextId: string | null;
  /** Currently selected subtitle (e.g. from timeline click); syncs with transcript panel. */
  selectedSubtitleId: string | null;
  /** Original subtitle segments per language (from SRT). */
  subtitleOriginalsByLanguage?: Record<string, ClippingStudioSubtitleEvent[]>;
  /** Merged subtitle segments per language (originals + edits). */
  subtitleTracksByLanguage?: Record<string, ClippingStudioSubtitleEvent[]>;
  /** Currently selected subtitle language (for multi-language mode). */
  activeSubtitleLanguage?: string;
  /** Per-track subtitle edits only (overlay on originals). */
  subtitleEdits?: Record<string, ClippingStudioSubtitleEvent[]>;
}

export function createInitialClippingStudioState(): ClippingStudioState {
  return {
    mode: "SOURCE",
    media: null,
    projectId: null,
    mainSource: null,
    findClipRecentPrompts: null,
    fps: null,
    projectTitle: "",
    clips: [],
    markers: [],
    subtitles: [],
    text: [],
    graphics: [],
    audio: [],
    mainAudioTracks: [],
    destinationVariants: {},
    appendedMamSegments: [],
    pendingInMs: null,
    pendingOutMs: null,
    selectedClipId: null,
    selectedMarkerId: null,
    isPlaying: false,
    currentTimeMs: 0,
    transcriptTracks: [],
    sourceAudioLanguage: "",
    activeTranscriptLanguage: "",
    isRegeneratingTranscript: false,
    loadingTranscriptTracks: false,
    transcriptLoadError: null,
    loadingSubtitleTracks: false,
    subtitleLoadError: null,
    subtitleStyle: null,
    selectedGraphicId: null,
    clipPreview: null,
    selectedTextId: null,
    selectedSubtitleId: null,
    subtitleOriginalsByLanguage: undefined,
    subtitleTracksByLanguage: undefined,
    activeSubtitleLanguage: undefined,
    subtitleEdits: undefined,
  };
}

function copyReframeConfig(c: ReframeConfig): ReframeConfig {
  return {
    ...c,
    crop: c.crop ? { ...c.crop } : undefined,
    topCrop: c.topCrop ? { ...c.topCrop } : undefined,
    bottomCrop: c.bottomCrop ? { ...c.bottomCrop } : undefined,
  };
}

/** Deep-copy a DestinationVariant for undo snapshot. */
function copyDestinationVariant(v: DestinationVariant): DestinationVariant {
  const textOverrides = v.textOverrides
    ? Object.fromEntries(
        Object.entries(v.textOverrides).map(([id, part]) => [id, { ...part }]),
      )
    : undefined;
  const graphicOverrides = v.graphicOverrides
    ? Object.fromEntries(
        Object.entries(v.graphicOverrides).map(([id, part]) => [id, { ...part }]),
      )
    : undefined;
  const textOnly = v.textOnly?.map((t) => ({ ...t })) ?? undefined;
  const graphicOnly = v.graphicOnly?.map((g) => ({ ...g })) ?? undefined;
  const textExcludedIds = v.textExcludedIds ? [...v.textExcludedIds] : undefined;
  const graphicExcludedIds = v.graphicExcludedIds ? [...v.graphicExcludedIds] : undefined;
  const segmentReframes = v.segmentReframes
    ? Object.fromEntries(
        Object.entries(
          normalizeSegmentReframes(
            v.segmentReframes as Record<string, ReframeConfig | ReframeKeyframe[]>,
          ),
        ).map(([id, keyframes]) => [
          id,
          keyframes.map((kf) => ({
            timeMs: kf.timeMs,
            config: copyReframeConfig(kf.config),
          })),
        ]),
      )
    : undefined;
  return {
    textOverrides,
    graphicOverrides,
    textOnly,
    graphicOnly,
    textExcludedIds,
    graphicExcludedIds,
    segmentReframes,
  };
}

/** Builds a deep copy of the undoable parts of state for history. */
export function getUndoableSnapshot(
  state: ClippingStudioState,
): ClippingStudioUndoableSnapshot {
  const subtitleEdits = state.subtitleEdits
    ? Object.fromEntries(
        Object.entries(state.subtitleEdits).map(([k, v]) => [
          k,
          v.map((s) => ({ ...s })),
        ]),
      )
    : undefined;
  const destinationVariants = state.destinationVariants
    ? Object.fromEntries(
        Object.entries(state.destinationVariants).map(([k, v]) => [
          k,
          copyDestinationVariant(v),
        ]),
      )
    : {};
  return {
    projectTitle: state.projectTitle,
    clips: state.clips.map((c) => ({ ...c })),
    markers: state.markers.map((m) => ({ ...m })),
    subtitles: state.subtitles.map((s) => ({ ...s })),
    subtitleEdits,
    subtitleStyle: state.subtitleStyle ? { ...state.subtitleStyle } : null,
    text: state.text.map((t) => ({ ...t })),
    graphics: state.graphics.map((g) => ({ ...g })),
    audio: state.audio.map((a) => ({ ...a })),
    destinationVariants,
    appendedMamSegments: (state.appendedMamSegments ?? []).map((s) => ({ ...s })),
  };
}

/** Returns a partial state that applies the given snapshot (for undo/redo). */
export function applyUndoableSnapshot(
  snapshot: ClippingStudioUndoableSnapshot,
): Partial<ClippingStudioState> {
  const subtitleEdits = snapshot.subtitleEdits
    ? Object.fromEntries(
        Object.entries(snapshot.subtitleEdits).map(([k, v]) => [
          k,
          v.map((s) => ({ ...s })),
        ]),
      )
    : undefined;
  const destinationVariants = snapshot.destinationVariants
    ? Object.fromEntries(
        Object.entries(snapshot.destinationVariants).map(([k, v]) => [
          k,
          copyDestinationVariant(v),
        ]),
      )
    : {};
  return {
    projectTitle: snapshot.projectTitle,
    clips: snapshot.clips.map((c) => ({ ...c })),
    markers: snapshot.markers.map((m) => ({ ...m })),
    subtitles: snapshot.subtitles.map((s) => ({ ...s })),
    subtitleEdits,
    subtitleStyle: snapshot.subtitleStyle
      ? { ...snapshot.subtitleStyle }
      : null,
    text: snapshot.text.map((t) => ({ ...t })),
    graphics: snapshot.graphics.map((g) => ({ ...g })),
    audio: snapshot.audio.map((a) => ({ ...a })),
    destinationVariants,
    appendedMamSegments: (snapshot.appendedMamSegments ?? []).map((s) => ({ ...s })),
  };
}


